/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, SelectorOptions, State, StateContext, Store } from '@ngxs/store';
import {
    DataFieldValueModel,
    DataFormDefinition,
    DataFormModel,
    DataFormStateModel,
    DataFormValueModel,
    LoadDataFormPayload,
} from './data-form.model';
import {
    ClearDataFormList,
    LoadDataForm,
    LoadDataFormList,
    PrefillFormFields,
    UpdateFormFieldValue,
} from './data-form.actions';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { RestService } from '../../shared/services/rest.service';
import { LoggerService } from '../../shared/services/logger.service';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthState } from '../../../core/security/state/auth.state';
import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';

@State<DataFormStateModel>({
    name: 'dataForm',
    defaults: {
        names: [],
        definitions: {},
    },
})
@Injectable()
export class DataFormState implements NgxsOnInit {
    validated$: Observable<boolean> = inject(Store).select(AuthState.validated);

    constructor(private rest: RestService) {}

    ngxsOnInit({ dispatch }: StateContext<any>): void | any {
        this.validated$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                tap(() => setTimeout(() => dispatch(new LoadDataFormList()), 0))
            )
            .subscribe();
    }

    @Action(LoadDataFormList)
    loadDataFormList({ getState, setState }: StateContext<DataFormStateModel>) {
        return this.rest.apiGet<string[]>('dataform/list').pipe(
            tap((formNames) => {
                const state = DashGisUtil.clone(getState());
                state.names = formNames || [];
                setState(state);
            })
        );
    }

    @Action(ClearDataFormList)
    clearDataFormList({ getState, setState }: StateContext<DataFormStateModel>) {
        const state = DashGisUtil.clone(getState());
        state.names = [];
        state.definitions = {};
        setState(state);
    }

    @Action(LoadDataForm)
    loadDataForm({ getState, setState }, action: LoadDataForm) {
        return this.loadDataFormInternal({ getState, setState }, action.payload);
    }

    @Action(UpdateFormFieldValue)
    updateFieldValue({ getState, setState }, action: UpdateFormFieldValue) {
        const state = DashGisUtil.clone<DataFormStateModel>(getState());
        const formName = action.payload.formName;
        const formData = action.payload.data;
        const valueModel = state.definitions[formName].value;
        const valueField = valueModel.fields.find((f) => f.name === formData.name);
        valueField.value = formData.value;
        valueField.dirty = formData.dirty;
        setState(state);
    }

    @Action(PrefillFormFields)
    prefillFormFields({ getState, setState }, action: PrefillFormFields) {
        const state = DashGisUtil.clone<DataFormStateModel>(getState());
        const formName = action.payload.formName;
        action.payload.data.forEach((formData) => {
            const definitionField = state.definitions[formName].definition.fields.find(
                (f) => f.fieldName === formData.name
            );
            if (definitionField) {
                if (definitionField.dataType === 'date') {
                    definitionField.currentValue = formData.value;
                    definitionField.newValue = formData.value;
                }
                definitionField.currentValue = formData.value;
                definitionField.newValue = formData.value;
            }
            const valueField = state.definitions[formName].value.fields.find((f) => f.name === formData.name);
            if (valueField) {
                valueField.value = formData.value;
            }
        });
        setState(state);
    }

    private loadDataFormInternal({ getState, setState }, payload: LoadDataFormPayload): Observable<DataFormDefinition> {
        const state: DataFormStateModel = DashGisUtil.clone(getState());
        const params = {
            ...(payload.keys && { keys: payload.keys.join(',') }),
            ...(payload.initialSelection && { initialSelection: JSON.stringify(payload.initialSelection) }),
        };
        return this.rest.apiGet<DataFormDefinition>(`dataform/definition/${payload.name}`, params).pipe(
            map((definition) => DataFormState.transformData(definition)),
            tap((definition) => {
                const formState: DataFormModel = {} as DataFormModel;
                formState.definition = definition;
                formState.keys = payload.keys;
                const valueModel: DataFormValueModel = DataFormState.createValueModel(definition);
                valueModel.keys = payload.keys;
                formState.value = valueModel;
                state.definitions[payload.name] = formState;
                setState(state);
            })
        );
    }

    @Selector([DataFormState])
    static names(state: DataFormStateModel): string[] {
        return state.names;
    }

    @Selector([DataFormState])
    static definitions(state: DataFormStateModel) {
        return state.definitions;
    }

    @Selector([DataFormState])
    static definition(state: DataFormStateModel): (string) => DataFormDefinition {
        return (name: string) => {
            if (state.definitions[name]) {
                return state.definitions[name].definition;
            } else {
                return null;
            }
        };
    }

    @Selector([DataFormState])
    static keys(state: DataFormStateModel) {
        return (name: string) => {
            if (state.definitions[name]) {
                return state.definitions[name].keys;
            } else {
                return null;
            }
        };
    }

    @Selector([DataFormState])
    static value(state: DataFormStateModel) {
        return (name: string) => {
            if (state.definitions[name]) {
                const def = state.definitions[name];
                const newDef = DashGisUtil.clone(def);
                newDef.value.fields = def.value?.fields?.map((f) => {
                    if (DateTime.isDateTime(f.value)) {
                        const dt: any = f.value;
                        const newDt = DateTime.fromObject(
                            {
                                year: dt.c.year,
                                month: dt.c.month,
                                day: dt.c.day,
                            },
                            {
                                zone: 'utc',
                                locale: 'en-US',
                            }
                        );
                        const newField = { ...f };
                        newField.value = newDt;
                        return newField;
                    }
                    return f;
                });
                return newDef.value;
            } else {
                return null;
            }
        };
    }

    private static createValueModel(def: DataFormDefinition): DataFormValueModel {
        const formValueModel = {} as DataFormValueModel;
        formValueModel.name = def.name;
        formValueModel.fields = def.fields.map(
            (f) =>
                ({
                    name: f.fieldName,
                    value: f.currentValue,
                    dirty: false,
                } as DataFieldValueModel)
        );
        return formValueModel;
    }

    private static transformData(definition: DataFormDefinition) {
        // change dates to Luxon DateTime
        for (const field of definition.fields) {
            if (field.dataType === 'date') {
                if (field.currentValue && typeof field.currentValue === 'string') {
                    let inputValue = DateTime.fromSQL(field.currentValue);
                    if (!inputValue) {
                        inputValue = DateTime.fromISO(field.currentValue);
                    }
                    field.currentValue = inputValue;
                }
            }
            if (field.uiType === 'dropdown_multi') {
                field.currentValue = field.currentValue
                    ? (field.currentValue as string).split(',')
                    : [];
            }
        }
        return definition;
    }
}
